import React, { createContext, useCallback, useMemo, useState } from 'react';

const ModalContext = createContext(null);

const ModalProvider = ({ children }) => {
    const [modals, setModals] = useState([]);

    const openModal = useCallback((component, props = {}, options = {}) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        setModals((prevState) => [
            ...prevState,
            {
                id,
                component,
                props,
                options,
            },
        ]);

        return id;
    }, []);

    const updateModalProps = useCallback((modalId, updater) => {
        setModals((prevState) =>
            prevState.map((item) => {
                if (item.id !== modalId) {
                    return item;
                }

                const nextProps =
                    typeof updater === 'function'
                        ? updater(item.props)
                        : {
                              ...item.props,
                              ...updater,
                          };

                return {
                    ...item,
                    props: nextProps,
                };
            }),
        );
    }, []);

    const closeModal = useCallback((modalId) => {
        if (!modalId) {
            setModals([]);
            return;
        }

        setModals((prevState) => prevState.filter((item) => item.id !== modalId));
    }, []);

    const value = useMemo(
        () => ({
            openModal,
            closeModal,
            updateModalProps,
        }),
        [openModal, closeModal, updateModalProps],
    );

    return (
        <ModalContext.Provider value={value}>
            {children}
            {modals.map((item) => {
                const Component = item.component;
                const defaultProps = {
                    isOpen: true,
                    onClose: () => closeModal(item.id),
                    toggle: () => closeModal(item.id),
                    closeModal: () => closeModal(item.id),
                };

                const builtProps = item.options?.mapProps
                    ? item.options.mapProps({
                          props: item.props,
                          close: () => closeModal(item.id),
                          updateProps: (nextUpdater) =>
                              updateModalProps(item.id, nextUpdater),
                          modalId: item.id,
                          isOpen: true,
                      })
                    : {
                          ...item.props,
                      };

                return (
                    <Component
                        key={item.id}
                        {...defaultProps}
                        {...builtProps}
                    />
                );
            })}
        </ModalContext.Provider>
    );
};

export { ModalContext, ModalProvider };
